import api from '@api';
import ExpandButton from '@components/common/ExpandButton';
import type { MenuGroupItem, MenuItem } from '@components/layout/sidebar/Menu';
import Menu from '@components/layout/sidebar/Menu';
import { Log } from '@enums';
import BinIcon from '@icons/bin.svg?react';
import CloseIcon from '@icons/close-sidebar.svg?react';
import HistoryIcon from '@icons/history.svg?react';
import NewTabIcon from '@icons/new-tab.svg?react';
import PlusIcon from '@icons/plus.svg?react';
import SparksIcon from '@icons/sparks.svg?react';
import { useChatStore } from '@store/chat';
import { useProfileStore } from '@store/profile';
import { useShellStore } from '@store/shell';
import { PrimaryButton as Button } from '@ui/buttons';
import * as utils from '@utils';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatListItem } from 'types/schema';
import Styled from './Sidebar.styled';

function Sidebar() {
  const chatStore = useChatStore();
  const shellStore = useShellStore();
  const feat = useProfileStore((state) => state.feat);
  const [agentVersions, setAgentVersions] = useState<string[]>();
  const ref = useRef<HTMLDivElement>(null);

  if (shellStore.isSidebarOpen && ref.current) {
    ref.current.focus();
  }

  const startChat = useCallback(() => {
    // The 'popstate' event is not fired on localhost
    if (import.meta.env.DEV) {
      chatStore.clear();
      shellStore.reset();
    }

    utils.pushPath(shellStore.pathname);
  }, [chatStore.clear, shellStore.reset, shellStore.pathname]);

  const openChat = useCallback(
    async (chatId: string) => {
      chatStore.setSessionIsLoading(true);

      try {
        const chat = await api.chats.getById(chatId);
        const transformedChat = {
          ...chat,
          title: chat.title,
          messages: chat.messages.map((item) => ({
            ...item.message,
            metadata: {
              ...item.message.metadata,
              ['from_saved_chat']: true
            }
          }))
        };
        // React will batch these store updates
        // and trigger only one re-rendering
        chatStore.setCurrentChat(transformedChat, true);
      } catch (_22) {
        shellStore.setToast({
          isOpen: true,
          isError: true,
          message: 'Sorry, something went wrong.'
        });
        utils.pushPath(shellStore.pathname);
      } finally {
        chatStore.setSessionIsLoading(false);
      }
    },
    [
      chatStore.setCurrentChat,
      chatStore.setSessionIsLoading,
      shellStore.setToast,
      shellStore.pathname
    ]
  );

  const handleChangedPathname = useCallback(() => {
    const chatId = utils.getPathnameId();

    if (chatId) {
      openChat(chatId);
      utils.log('Opened chat session using the current URL.', chatId);
    } else if (
      !utils.isSettingsPath(window.location.pathname) &&
      window.location.pathname !== shellStore.pathname
    ) {
      // Change pathname to default
      utils.replacePath(shellStore.pathname);
    }
  }, [openChat, shellStore.pathname]);

  const handleSelectChatSession = useCallback(
    (item: ChatListItem, event?: MouseEvent<HTMLElement>) => {
      event?.stopPropagation();

      if (shellStore.isSidebarOpen) {
        shellStore.setIsSidebarOpen(false);
      }

      if (shellStore.isRightPanelOpen) {
        shellStore.setIsRightPanelOpen(false);
      }

      utils.log(`Opened chat '${item.title}'.`, item.id);

      api.analytics.openedRecentChat({
        place: 'side_panel',
        ['chat_id']: item.id
      });

      // Update URL in address bar
      utils.pushPath(`${shellStore.pathname}/chat/${item.id}`);

      // Render the selected chat session
      openChat(item.id);
    },
    [
      openChat,
      shellStore.isSidebarOpen,
      shellStore.isRightPanelOpen,
      shellStore.pathname,
      shellStore.setIsSidebarOpen,
      shellStore.setIsRightPanelOpen
    ]
  );

  const handleClickDeleteAction = useCallback(
    async (item: MenuGroupItem, deleteItem?: () => void) => {
      await api.chats.deleteById(item.id);

      // Hide item from the menu list
      deleteItem?.();

      if (item.active) {
        utils.log('Deleted current chat.', item.id);
        // Deleting currernt chat will take user back to intro view
        utils.pushPath(shellStore.pathname);
        chatStore.clear();
        shellStore.setIsSidebarOpen(false);
      } else {
        utils.log(`Deleted chat "${item.title}"`, item.id);
      }
    },
    [chatStore.clear, shellStore.setIsSidebarOpen, shellStore.pathname]
  );

  const actionList: MenuGroupItem['actionList'] = useMemo(
    () => [
      {
        label: 'Open in new tab',
        value: 'new-tab',
        selected: false,
        startIcon: <NewTabIcon />,
        onClick: (groupItem) =>
          window.open(`${shellStore.pathname}/chat/${groupItem.id}`, '_blank')
      },
      {
        label: 'Delete',
        value: 'delete',
        selected: false,
        startIcon: <BinIcon />,
        onClick: (groupItem, deleteItem) =>
          handleClickDeleteAction(
            {
              ...groupItem,
              active: groupItem.id === chatStore.chat.id
            },
            deleteItem
          )
      }
    ],
    [chatStore.chat.id, shellStore.pathname, handleClickDeleteAction]
  );

  // Avoid unnecessary re-mappings
  const memoizedChatList = useMemo(
    () =>
      chatStore.history.map(({ title, chats }) => ({
        title,
        items: chats?.map((item) => ({
          ...item,
          title: utils.formatChatTitle(item),
          actionList
        }))
      })),
    [actionList, chatStore.history]
  );

  const handleSelectAgentVersion = useCallback(
    (item: ChatListItem, event?: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      chatStore.setAgentVersion(item.title);
      window.localStorage.setItem('octopus.agent', item.title);
      utils.log(`Agent version changed to '${item.title}'.`);
    },
    [chatStore.setAgentVersion]
  );

  const handleClickMenuItem = useCallback(
    () => shellStore.setIsSidebarExpanded(true),
    [shellStore.setIsSidebarExpanded]
  );

  const handleLazyLoading = useCallback(async () => {
    utils.log('Lazy loading triggered.', Log.Notice);

    try {
      // Lazy loading of the chat list
      const page = api.chats.pageGenerator.next();
      const olderChats = await api.chats.getAll(page.value);

      // Check if the chat list is empty
      const isEmptyList = olderChats
        .map(({ chats }) => chats)
        .every((item) => item.length === 0);

      if (!isEmptyList) {
        chatStore.setHistory((history) => {
          const augmentedHistory = [...history].map((item, index) => ({
            ...item,
            chats: [...item.chats, ...olderChats[index].chats]
          }));

          return augmentedHistory;
        });
      }

      return { hasMore: !isEmptyList };
    } catch (_error) {
      shellStore.setToast({
        isOpen: true,
        isError: true,
        message: 'Sorry, something went wrong.'
      });

      return { hasMore: false };
    }
  }, [shellStore.setToast, chatStore.setHistory]);

  const memoizedMenuItems: MenuItem<MenuGroupItem>[] = useMemo(
    () => [
      {
        icon: <SparksIcon className="icon" />,
        hidden: !feat.selectAgentVersion.enabled,
        label: 'Agent Versions',
        groups: agentVersions
          ? [
              {
                items: agentVersions.map((agentName) => ({
                  title: agentName,
                  id: agentName
                }))
              }
            ]
          : [],
        activeGroupItemId: chatStore.agentVersion,
        onClickGroupItem: handleSelectAgentVersion
      },
      {
        active: true,
        icon: <HistoryIcon className="icon" />,
        hidden: memoizedChatList.length === 0,
        label: 'Recent',
        groups: memoizedChatList,
        activeGroupItemId: chatStore.chat.id,
        pathname: `${shellStore.pathname}/chat`,
        onClickGroupItem: handleSelectChatSession,
        onLazyLoading: handleLazyLoading
      }
    ],
    [
      agentVersions,
      feat.selectAgentVersion.enabled,
      memoizedChatList,
      chatStore.chat.id,
      chatStore.agentVersion,
      shellStore.pathname,
      handleSelectChatSession,
      handleSelectAgentVersion,
      handleLazyLoading
    ]
  );

  useEffect(() => {
    handleChangedPathname();

    const handlePopState = () => {
      // Notice, this wouldn't work using 'history.state' as
      // this would be persistent to history changes.
      if (sessionStorage.getItem('octopus.popstate_event.ignore_duplicate')) {
        sessionStorage.removeItem('octopus.popstate_event.ignore_duplicate');
        utils.log(
          'Navigation was blocked due to a popstate event' +
            ' ' +
            'to avoid unintended history changes.',
          Log.Warning
        );
        return;
      }

      handleChangedPathname();
    };

    // Capture the URL changes
    window.addEventListener('popstate', handlePopState);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handleChangedPathname]);

  useEffect(() => {
    if (feat.selectAgentVersion.enabled) {
      // Retrieve all available agents
      api.user.getAgentVersions().then((agentVersions) => {
        const agentVersion = utils.getStorageItem('octopus.agent');

        // Set current agent version
        if (agentVersion) {
          chatStore.setAgentVersion(agentVersion);
        } else if (agentVersions?.length) {
          const [defaultAgentVersion] = agentVersions;
          chatStore.setAgentVersion(defaultAgentVersion);
          window.localStorage.setItem('octopus.agent', defaultAgentVersion);
        }

        setAgentVersions(agentVersions);
      });
    }
  }, [chatStore.setAgentVersion, feat.selectAgentVersion.enabled]);

  useEffect(() => {
    // Retrieve recent chat list from the server
    api.chats.getAll().then(chatStore.setHistory);
  }, [chatStore.setHistory]);

  return (
    <>
      <Styled.Sidebar
        ref={ref}
        isBusy={chatStore.isLoading}
        isExpanded={shellStore.isSidebarExpanded || shellStore.isSidebarOpen}
        isOpen={shellStore.isSidebarOpen}
        tabIndex={0}
        data-testid="sidebar-container">
        <Styled.CloseButton
          tabIndex={0}
          data-testid="sidebar-close-arrow"
          onClick={() => shellStore.setIsSidebarOpen(false)}>
          <CloseIcon />
        </Styled.CloseButton>
        <ExpandButton
          className="sidebar-collapse-button"
          isActive={shellStore.isSidebarExpanded}
          testId="sidebar-collapse"
          yPosition={63}
          onClick={() =>
            shellStore.setIsSidebarExpanded(!shellStore.isSidebarExpanded)
          }
        />
        <Styled.Action>
          <Button
            className="button"
            testId="new-chat"
            disabled={chatStore.isLoading || chatStore.sessionIsLoading}
            startIcon={<PlusIcon />}
            onClick={(event) => {
              event.preventDefault();
              startChat();
            }}>
            {/* To support 'Open Link in New Tab' */}
            <Styled.LinkMask
              href={shellStore.pathname}
              title="Start new chat"
            />
            <span className="button-text">New chat</span>
          </Button>
        </Styled.Action>
        <Menu
          isExpanded={shellStore.isSidebarExpanded}
          items={memoizedMenuItems}
          onClickMenuItem={handleClickMenuItem}
        />
      </Styled.Sidebar>
      <Styled.Backdrop
        isVisible={shellStore.isSidebarOpen}
        onClick={() => shellStore.setIsSidebarOpen(false)}
      />
    </>
  );
}

export default Sidebar;
